import React, {
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';
import styled from 'styled-components';

import { useAppSelector } from '@/hooks/useReduxHooks';

import { getComma, removeComma } from '@/util/format';
import { getRefundAmount } from '@/util/getRefundAmount';
import { useForm, useFormContext } from 'react-hook-form';
import Typography from '../common/Typography';
import LoadingView from '../common/LoadingView';
import { useMutation } from 'react-query';
import { checkThirdRefundAttempt } from '@/api/refund';

type Props = {
  isPayment: boolean;
  setLeafletModalInfo: Dispatch<
    SetStateAction<{
      active: boolean;
      isOver600: boolean;
      isKioskError: boolean;
    }>
  >;
  errorMessage: {
    isSubmit: boolean;
    message: string;
  };
  onClickSaveBtn: () => void;
  onClickRefundBtn: any;
  isLoading: boolean;
  isPassportCheck: boolean;
  isFetchedStorageData: boolean;
  supplyList: {
    productType: string;
    price: string;
    supplyPrice: string;
    vatPrice: string;
    isCheck: boolean;
  }[];
  isCustomKor: boolean;
  setIsDuplicateRefund: (value: boolean) => void;
};
function AmountDetail({
  isCustomKor,
  setLeafletModalInfo,
  errorMessage,
  onClickSaveBtn,
  onClickRefundBtn,
  isLoading,
  isPassportCheck,
  isFetchedStorageData,
  supplyList,
  setIsDuplicateRefund,
}: Props) {
  const [totalPayPrice, setTotalPayPrice] = useState<number | ''>('');
  const [paymentCard, setPaymentCard] = useState('');
  const [paymentCash, setPaymentCash] = useState('');
  const [isManualInput, setIsManualInput] = useState(false);

  let paymentCardRef = useRef<string | number>(0);
  let paymentCashRef = useRef<string | number>(0);
  const methods = useFormContext();
  const { register, setValue } = useForm();
  const { temporaryStorage } = useAppSelector((state) => state.refund);
  const { passportNumber, supplyDate } = methods.getValues();

  // TODO - 임시저장 환급 에러 참고
  // 임시저장
  useEffect(() => {
    if (temporaryStorage.supplyDate) {
      let newTotalPayPrice = 0;
      temporaryStorage.supply.forEach((item: any) => {
        newTotalPayPrice += Number(String(item.price).replace(/(,)/g, ''));
      });
      setTotalPayPrice(newTotalPayPrice);
      setPaymentCash(temporaryStorage.paymentCash || '0');
      setPaymentCard(temporaryStorage.paymentCard || '0');
      setValue('paymentCash', temporaryStorage.paymentCash || '0');
      setValue('paymentCard', temporaryStorage.paymentCard || '0');
    }
    // console.log(temporaryStorage.paymentCash);
  }, [temporaryStorage]);

  const supplyData = [...methods.getValues('supply')];

  // 금액 계산 (수동 입력 모드가 아닐 때만)
  useEffect(() => {
    if (isManualInput) return;

    let newTotalPayPrice = 0;
    methods.getValues('supply').forEach((item: any) => {
      newTotalPayPrice += Number(String(item.price).replace(/(,)/g, ''));
    });
    setTotalPayPrice(newTotalPayPrice);
  }, [supplyData, isManualInput]);

  const paymentTypeCheck = () => {
    const price = typeof totalPayPrice === 'number' ? totalPayPrice : 0;
    return price - getRefundAmount(price);
  };

  useEffect(() => {
    // 초기화 현금
    const price = typeof totalPayPrice === 'number' ? totalPayPrice : 0;
    if (!totalPayPrice || price <= 0) {
      setPaymentCash('');
      setPaymentCard('');
      paymentCashRef.current = '';
      paymentCardRef.current = '';
      methods.setValue('paymentCash', paymentCashRef.current);
      methods.setValue('paymentCard', paymentCardRef.current);
      return;
    }

    const cashValue = paymentCash ? +paymentCash.replace(/(,)/g, '') : 0;
    const newCardValue = getComma(paymentTypeCheck() - cashValue);

    // 카드금액이 -가 됬을때
    if (+newCardValue.replace(/(,)/g, '') < 0) {
      let cashValue = totalPayPrice - getRefundAmount(totalPayPrice);
      setPaymentCard('');
      setPaymentCash(getComma(cashValue));
      paymentCardRef.current = '';
      paymentCashRef.current = getComma(cashValue);
      methods.setValue('paymentCash', paymentCashRef.current);
      methods.setValue('paymentCard', paymentCardRef.current);
      return;
    }

    setPaymentCard(newCardValue);
    paymentCardRef.current = newCardValue;

    if (methods) {
      methods.setValue('paymentCard', paymentCardRef.current);
    }
  }, [totalPayPrice]);

  const onChangePrice = (e: any) => {
    // 금액이 0으로 시작하는거 방지
    const newtarget =
      e.target.value.charAt(0) === '0'
        ? e.target.value.substring(1)
        : e.target.value;

    const price = typeof totalPayPrice === 'number' ? totalPayPrice : 0;
    let cashValue = newtarget.replace(/[^0-9]/g, '').replace(/(,)/g, '');
    let cardValue = +paymentTypeCheck() - +cashValue;

    const totalValue = price - getRefundAmount(price);
    if (cashValue >= totalValue) {
      cashValue = price - getRefundAmount(price);
      cardValue = 0;
    }

    setPaymentCard(getComma(cardValue));
    setPaymentCash(getComma(cashValue));

    paymentCardRef.current = getComma(cardValue);
    paymentCashRef.current = getComma(cashValue);

    methods.setValue('paymentCard', paymentCardRef.current);
    methods.setValue('paymentCash', paymentCashRef.current);
  };

  const isDisabledRefundButton = useMemo(() => {
    const has0price =
      !totalPayPrice ||
      totalPayPrice === 0 ||
      removeComma(paymentCard) + removeComma(paymentCash) === 0;
    return !isPassportCheck || isLoading || has0price;
  }, [isPassportCheck, isLoading, totalPayPrice, paymentCard, paymentCash]);

  // TODO - 임시저장 환급 에러 참고
  // console.log(paymentCash);

  const { mutate: mutateCheckThirdRefundAttempt } = useMutation(
    checkThirdRefundAttempt,
  );

  const onClickReset = () => {
    // 시술금액 초기화
    setTotalPayPrice('');
    methods.setValue('totalPayPrice', '');

    // 카드/현금 결제 초기화
    setPaymentCash('');
    setPaymentCard('');

    paymentCashRef.current = '';
    paymentCardRef.current = '';

    methods.setValue('paymentCash', '');
    methods.setValue('paymentCard', '');
  };

  const handleClickRefund = async () => {
    const products = [{ productType: 'Others', count: 1 }];
    return new Promise((resolve, reject) => {
      mutateCheckThirdRefundAttempt(
        {
          supplyDate,
          passportNumber: passportNumber.toUpperCase(),
          products,
        },
        {
          onSuccess: async () => {
            await onClickRefundBtn();
            return resolve('ok');
          },
          onError: (error: any) => {
            if (error.response.data.code === 'H0011') {
              setIsDuplicateRefund(true);
            }
            return reject(error);
          },
        },
      );
    });
  };

  const onChangeTotalPayPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsManualInput(true);
    const value = e.target.value.replace(/[^0-9]/g, '');
    const numericValue = value === '' ? '' : Number(value);
    setTotalPayPrice(numericValue);
    // react-hook-form에도 값 동기화
    methods.setValue('totalPayPrice', value);
  };

  return (
    <AmountDetailWrapper>
      <PaymentContainer isFullPayment={false}>
        <PaymentHeader>
          <ResetButton onClick={onClickReset}>
            <Typography
              size='16px'
              lineHeight='24px'
              fontWeight='500'
              color='#999'
            >
              초기화
            </Typography>
            <ResetIcon />
          </ResetButton>
        </PaymentHeader>

        <PaymentContentWrapper>
          <SingleInputRow>
            <SingleInputLabel>시술금액</SingleInputLabel>
            <LargeInputWrapper isActive={true}>
              <LargeInput
                type='text'
                {...register('totalPayPrice')}
                onChange={onChangeTotalPayPrice}
                value={totalPayPrice ? getComma(totalPayPrice) : ''}
                placeholder='시술 금액을 입력해주세요'
              />
              <LargeUnitText>원</LargeUnitText>
            </LargeInputWrapper>
          </SingleInputRow>

          <PaymentRowGroup>
            <SmallPaymentRow>
              <SmallPaymentLabel>카드결제</SmallPaymentLabel>
              <SmallInputWrapper readOnly>
                <SmallInput
                  readOnly
                  {...register('paymentCard')}
                  type='text'
                  value={paymentCard}
                  placeholder='자동 입력'
                />
                <SmallUnitText>원</SmallUnitText>
              </SmallInputWrapper>
            </SmallPaymentRow>

            <SmallPaymentRow>
              <SmallPaymentLabel>현금결제</SmallPaymentLabel>
              <SmallInputWrapper isActive={!!totalPayPrice}>
                <SmallInput
                  {...register('paymentCash')}
                  type='text'
                  onChange={onChangePrice}
                  value={paymentCash}
                  placeholder='현금 결제 금액 입력'
                  disabled={!totalPayPrice}
                />
                <SmallUnitText>원</SmallUnitText>
              </SmallInputWrapper>
            </SmallPaymentRow>

            <SmallPaymentRow>
              <ExpectedRefundLabel>예상 환급액</ExpectedRefundLabel>
              <SmallInputWrapper readOnly>
                <SmallInput
                  readOnly
                  type='text'
                  value={
                    totalPayPrice && typeof totalPayPrice === 'number'
                      ? getComma(getRefundAmount(totalPayPrice))
                      : ''
                  }
                  placeholder='자동 입력'
                />
                <SmallUnitText>원</SmallUnitText>
              </SmallInputWrapper>
            </SmallPaymentRow>
          </PaymentRowGroup>

          {isFetchedStorageData && !isPassportCheck && (
            <AlertText>
              <Red>[여권 확인] 버튼을 먼저 눌러주세요</Red>
            </AlertText>
          )}
        </PaymentContentWrapper>
      </PaymentContainer>

      <BottomActionContainer>
        <FinalAmountWrapper>
          <FinalAmountLabel>최종 결제 금액</FinalAmountLabel>
          <LargeInputWrapper readOnly>
            <TotalAmountInput
              readOnly
              type='text'
              value={
                totalPayPrice && typeof totalPayPrice === 'number'
                  ? getComma(totalPayPrice - getRefundAmount(totalPayPrice))
                  : ''
              }
              placeholder='자동 입력'
              disabled
            />
            <TotalAmountUnitText>원</TotalAmountUnitText>
          </LargeInputWrapper>
        </FinalAmountWrapper>

        <ButtonWrapper>
          {Boolean(errorMessage.message) && (
            <ErrorMessage>{errorMessage.message}</ErrorMessage>
          )}

          <Button
            isValidate={!isDisabledRefundButton}
            disabled={isDisabledRefundButton}
            onClick={handleClickRefund}
          >
            {isLoading ? <LoadingView /> : '할인 등록하기'}
          </Button>
        </ButtonWrapper>
      </BottomActionContainer>
    </AmountDetailWrapper>
  );
}

const AmountDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PaymentContainer = styled.div<{ isFullPayment?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  padding: 36px;
  border-radius: 16px;
  gap: 24px;
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const PaymentContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SingleInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const SingleInputLabel = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.mono[80]};
  white-space: nowrap;
`;

const LargeInputWrapper = styled.div<{
  readOnly?: boolean;
  isActive?: boolean;
}>`
  flex: 1;
  height: 52px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 24px;
  border-radius: 4px;
  border: 2px solid ${(props) => props.theme.mono[20]};
  background-color: #ffffff;
  &:focus-within {
    border-color: ${(props) => props.theme.blue[100]};
  }
`;

const LargeInput = styled.input`
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  text-align: right;

  color: #030303;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 28px */
  letter-spacing: -0.4px;

  &::placeholder {
    color: #cbccce;
  }
`;

const LargeUnitText = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => props.theme.mono[80]};
  white-space: nowrap;
`;

const TotalAmountInput = styled.input`
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  text-align: right;

  color: #030303;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;

  &::placeholder {
    color: #030303;
  }
`;

const TotalAmountUnitText = styled.span`
  padding-top: 4px;
  color: var(--Mono-50, #80848a);
  text-align: right;

  /* WebBody/16_MD */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
`;

const PaymentRowGroup = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;

const SmallPaymentRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex: 1;
`;

const SmallPaymentLabel = styled.p`
  display: flex;
  align-items: center;
  color: var(--Mono-50, #80848a);

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
`;

const ExpectedRefundLabel = styled.p`
  display: flex;
  align-items: center;
  color: var(--Mono-80, #3a3b3e);
  text-align: right;

  /* Subtitle/subtitle-lg */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
  white-space: nowrap;
`;

const SmallInputWrapper = styled.div<{
  readOnly?: boolean;
  isActive?: boolean;
}>`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-radius: 4px;
  border: 1.5px solid
    ${(props) => (props.readOnly ? 'none' : props.theme.mono[20])};
  background-color: ${(props) =>
    props.readOnly ? props.theme.mono[5] : '#ffffff'};

  &:focus-within {
    border-color: ${(props) => props.theme.blue[100]};
  }
`;

const SmallInput = styled.input`
  flex: 1;
  height: 100%;
  width: 100%;
  border: none;
  background: transparent;
  text-align: right;
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.mono[35]};
  outline: none;

  &::placeholder {
    color: ${(props) => props.theme.mono[35]};
    font-weight: 500;
  }
`;

const SmallUnitText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.mono[35]};
  white-space: nowrap;
`;

const BottomActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background-color: #ffffff;
  border-radius: 16px;
  gap: 120px;
`;

const FinalAmountWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 24px;
`;

const FinalAmountLabel = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: ${(props) => props.theme.mono[80]};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
`;

const Red = styled.span`
  color: red;
`;

const AlertText = styled.p`
  color: ${(props) => props.theme.mono[80]};
  font-size: ${(props) => props.theme.fontSize.regular};
  margin-left: auto;
  font-weight: 500;
  text-align: right;
`;

const Button = styled.button<{
  isValidate?: boolean;
  temporaryStorage?: boolean;
}>`
  width: ${(props) => (props.temporaryStorage ? '120px' : '240px')};
  height: 52px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  color: ${(props) =>
    props.temporaryStorage ? props.theme.mono[80] : '#ffffff'};
  background-color: ${(props) =>
    props.temporaryStorage
      ? props.theme.mono[10]
      : props.isValidate
      ? props.theme.blue[100]
      : props.theme.blue[20]};
  border: ${(props) =>
    props.temporaryStorage ? `1px solid ${props.theme.mono[20]}` : 'none'};

  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  :hover {
    opacity: 0.7;
  }
`;

const ErrorMessage = styled.p`
  display: flex;
  justify-content: flex-end;
  color: ${(props) => props.theme.priamryColors.error};
`;

const ResetIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M10.0001 3.33334V0.833344L6.66675 4.16668L10.0001 7.50001V5.00001C12.7584 5.00001 15.0001 7.24168 15.0001 10C15.0001 10.8417 14.7917 11.6417 14.4167 12.3333L15.6334 13.55C16.2917 12.525 16.6667 11.3083 16.6667 10C16.6667 6.31668 13.6834 3.33334 10.0001 3.33334ZM10.0001 15C7.24175 15 5.00008 12.7583 5.00008 10C5.00008 9.15834 5.20842 8.35834 5.58342 7.66668L4.36675 6.45001C3.70842 7.47501 3.33341 8.69168 3.33341 10C3.33341 13.6833 6.31675 16.6667 10.0001 16.6667V19.1667L13.3334 15.8333L10.0001 12.5V15Z'
      fill='#999999'
    />
  </svg>
);

export default AmountDetail;
